import { Heading, Text, VStack, HStack, Icon, Image, Box } from "@chakra-ui/react";
import { MdRestaurantMenu as ServingsIcon, MdSchedule as PrepIcon, MdLocalFireDepartment as CookIcon } from 'react-icons/md';
import { Card } from "@chakra-ui/react/card";
import { getRecipeDisplayImageUrl } from "@/utils/imageUtils";
import { formatDuration } from "@/utils/formatDuration";
import { Recipe } from "@/types";
import StarRating from "@/components/StarRating";

interface RecipeCardProps {
  recipe: Recipe;
  onRecipeClick: (recipeId: string) => void;
  deleteButton?: React.ReactNode;
}

export default function RecipeCard({ recipe, onRecipeClick, deleteButton }: RecipeCardProps) {
  const imageUrl = getRecipeDisplayImageUrl(recipe);
  const hasImage = !!imageUrl;

  return (
    <Card.Root
      data-testid="recipe-card"
      cursor="pointer"
      onClick={() => onRecipeClick(recipe.id)}
      _hover={{ shadow: "sm", bg: "bg.muted" }}
      transition="background 0.15s, box-shadow 0.15s"
      position="relative"
      bg="bg.surface"
      border="1px solid"
      borderColor="border.muted"
      borderRadius="14px"
      display="flex"
      flexDirection="row"
      overflow="hidden"
    >
      {hasImage ? (
        <Box w="100px" h="100px" flexShrink={0} overflow="hidden" bg="bg.muted">
          <Image
            src={imageUrl as string}
            alt={recipe.title}
            w="100%"
            h="100%"
            objectFit="cover"
            display="block"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </Box>
      ) : (
        <Box
          w="100px"
          h="100px"
          flexShrink={0}
          data-testid="recipe-placeholder"
          style={{ background: "linear-gradient(135deg, #e8a020 0%, #d4741a 50%, #C85A1E 100%)" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="3"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <path d="M21 15l-5-5L5 21"/>
          </svg>
        </Box>
      )}

      <Card.Body p={3} flex="1" display="flex" minW={0}>
        <VStack align="start" gap={2} flex="1" minW={0}>
          <Heading
            fontFamily="heading"
            fontWeight="800"
            fontSize="14px"
            lineHeight="1.2"
            color="text.primary"
            lineClamp={2}
          >
            {recipe.title}
          </Heading>

          {recipe.rating && <StarRating rating={recipe.rating} size={14} />}

          {(recipe.servings || recipe.prepTime || recipe.cookTime) && (
            <HStack gap={3} flexWrap="wrap">
              {recipe.servings && (
                <HStack gap={1}>
                  <Icon as={ServingsIcon} boxSize={3} color="accent.secondary" />
                  <Text fontFamily="body" fontWeight="400" fontSize="11px" color="text.primary">
                    {String(recipe.servings)}
                  </Text>
                </HStack>
              )}
              {recipe.prepTime && (
                <HStack gap={1}>
                  <Icon as={PrepIcon} boxSize={3} color="accent.secondary" />
                  <Text fontFamily="body" fontWeight="400" fontSize="11px" color="text.primary">
                    {formatDuration(recipe.prepTime)}
                  </Text>
                </HStack>
              )}
              {recipe.cookTime && (
                <HStack gap={1}>
                  <Icon as={CookIcon} boxSize={3} color="accent.emphasis" />
                  <Text fontFamily="body" fontWeight="400" fontSize="11px" color="text.primary">
                    {formatDuration(recipe.cookTime)}
                  </Text>
                </HStack>
              )}
            </HStack>
          )}

        </VStack>
      </Card.Body>

      {deleteButton && (
        <Box position="absolute" top={2} right={2} zIndex={1} onClick={(e) => { e.stopPropagation(); }}>
          {deleteButton}
        </Box>
      )}
    </Card.Root>
  );
}
